import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { describeAdminProspectTargetingChips } from '../../services/admin/prospectTargetingFilters.js';
import {
  ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE,
  ADMIN_PROSPECT_CAMPAIGN_TARGETS_PAGE_SIZE,
  buildGenerateAdminProspectCampaignLettersRequest,
  buildGetAdminProspectCampaignLetterArchiveDownloadUrlRequest,
  buildGetAdminProspectCampaignLetterDownloadUrlRequest,
  buildListAdminProspectCampaignMailingsRequest,
  buildListAdminProspectCampaignTargetsRequest,
  buildPrepareAdminProspectCampaignMailingsRequest,
  buildRequestAdminProspectCampaignLetterArchiveRequest,
  buildRetryAdminProspectCampaignLetterArchiveRequest,
  buildRetryAdminProspectCampaignBuildRequest,
  buildRetryAdminProspectCampaignMailingBuildRequest,
  createAdminProspectCampaignOperationAttempt,
  generateAdminProspectCampaignLetters,
  generateAdminProspectCampaignOperationId,
  getAdminProspectCampaign,
  getAdminProspectCampaignLetterArchiveDownloadUrl,
  getAdminProspectCampaignLetterDownloadUrl,
  listAdminProspectCampaignMailings,
  listAdminProspectCampaignTargets,
  prepareAdminProspectCampaignMailings,
  requestAdminProspectCampaignLetterArchive,
  retryAdminProspectCampaignLetterArchive,
  retryAdminProspectCampaignBuild,
  retryAdminProspectCampaignMailingBuild,
} from '../../services/admin/prospectCampaignsService.js';
import { runAdminProspectCampaignLetterDownloadAttempt } from '../../services/admin/prospectCampaignLetterDownload.js';
import { formatAdminDate } from './adminUserDisplay.js';
import {
  ADMIN_DATE_PLACEHOLDER,
  ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS,
  ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS,
  ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MAX_MS,
  ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MS,
  ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MAX_MS,
  ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MS,
  ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MAX_MS,
  ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MS,
  adminProspectCampaignBuildStatusBadgeClass,
  adminProspectCampaignBuildStatusLabel,
  adminProspectCampaignErrorKey,
  adminProspectCampaignFailureMessage,
  adminProspectCampaignLetterArchiveErrorKey,
  adminProspectCampaignLetterArchiveStatusBadgeClass,
  adminProspectCampaignLetterArchiveStatusLabel,
  adminProspectCampaignLetterBuildStatusBadgeClass,
  adminProspectCampaignLetterBuildStatusLabel,
  adminProspectCampaignLetterErrorKey,
  adminProspectCampaignMailingBuildStatusBadgeClass,
  adminProspectCampaignMailingBuildStatusLabel,
  adminProspectCampaignMailingLetterStatusLabel,
  adminProspectCampaignMailingReadinessStatusBadgeClass,
  adminProspectCampaignMailingReadinessStatusLabel,
  adminProspectCampaignSelectionModeLabel,
  buildCampaignPdfLetterRows,
  campaignHasMailingReadinessSummary,
  campaignHasZeroMailableRecipients,
  campaignLettersSectionVisible,
  campaignTargetNeedsProspectFixAction,
  canGenerateAdminProspectCampaignLetters,
  canPrepareAdminProspectCampaignMailings,
  canRequestAdminProspectCampaignLetterArchive,
  canRetryAdminProspectCampaignLetterArchive,
  canRetryAdminProspectCampaignBuild,
  canRetryAdminProspectCampaignMailingBuild,
  classifyAdminCallableError,
  countMailingLetterStatuses,
  formatCampaignLetterRecipientMailing,
  formatCampaignLetterRecipientName,
  formatCampaignLetterTemplateLabel,
  formatCampaignTargetAddress,
  formatCampaignTargetLocality,
  formatCampaignTargetMailingReadinessReasons,
  formatCampaignTargetMailingReadinessRecipients,
  formatCampaignTargetOwnerMailing,
  formatCampaignTargetOwnerName,
  formatCampaignTargetUnits,
  isCampaignLetterArchiveDownloadable,
  isCampaignLetterDownloadable,
  adminProspectPublicResponseIntentLabel,
  adminProspectPublicResponseCopy,
  listMailingReceivedIntentBadges,
} from './adminProspectCampaignDisplay.js';
import {
  createAdminPublicResponseRequestTracker,
  createClosedAdminPublicResponsePanel,
  createLoadingAdminPublicResponsePanel,
  revealAdminPublicResponsePanelElement,
  shouldApplyAdminPublicResponseResult,
  shouldRevealAdminPublicResponsePanel,
} from './adminPublicResponseRequestOwnership.js';
import { getAdminProspectCampaignMailingPublicResponse } from '../../services/admin/prospectCampaignMailingPublicResponse.js';
import { formatCadCentsForDisplay } from '../../utils/publicCadMoney.js';

const UNIT_MIX_LABEL_KEYS = Object.freeze({
  oneAndHalf: 'unitOneAndHalf',
  twoAndHalf: 'unitTwoAndHalf',
  threeAndHalf: 'unitThreeAndHalf',
  fourAndHalf: 'unitFourAndHalf',
  fiveAndHalf: 'unitFiveAndHalf',
  sixAndHalfPlus: 'unitSixAndHalfPlus',
  commercial: 'unitCommercial',
  other: 'unitOther',
});

function formatAdminMoneyOrUnknown(cents, language, unknownLabel) {
  if (cents === null) return unknownLabel;
  return formatCadCentsForDisplay(cents, language) || unknownLabel;
}

function AdminPublicResponseDetailItem({ item, language, responseCopy }) {
  const financial = item.financialSnapshot;
  const profile = item.propertyProfile;
  return (
    <li className="rounded-lg border border-slate-200 p-3 text-sm">
      <p className="font-semibold text-slate-900">{adminProspectPublicResponseIntentLabel(item.intent, language)}</p>
      <div className="mt-2 space-y-1 text-slate-700">
        <p>
          <span className="font-medium">{responseCopy.intent}:</span>{' '}
          {adminProspectPublicResponseIntentLabel(item.intent, language)}
        </p>
        {item.submittedAt ? (
          <p>
            <span className="font-medium">{responseCopy.submittedAt}:</span> {item.submittedAt}
          </p>
        ) : null}
        {typeof item.schemaVersion === 'number' ? (
          <p>
            <span className="font-medium">{responseCopy.schemaVersion}:</span> {item.schemaVersion}
          </p>
        ) : null}
        {item.language ? (
          <p>
            <span className="font-medium">{responseCopy.languageLabel}:</span> {item.language.toUpperCase()}
          </p>
        ) : null}
      </div>
      <div className="mt-3">
        <p className="font-semibold text-slate-900">{responseCopy.contact}</p>
        <p className="mt-1">{item.contact?.fullName}</p>
        <p>{item.contact?.email}</p>
        {item.contact?.phone ? <p>{item.contact.phone}</p> : null}
        {item.intent === 'property_sale' && item.ownershipAttested ? (
          <p className="mt-1 text-xs text-slate-600">{responseCopy.ownershipAttested}</p>
        ) : null}
      </div>
      {item.intent === 'property_value' && item.schemaVersion === 3 && financial ? (
        <div className="mt-3 space-y-1">
          <p className="font-semibold text-slate-900">{responseCopy.financial}</p>
          <p>
            {responseCopy.grossAnnualIncome}:{' '}
            {formatAdminMoneyOrUnknown(financial.grossAnnualIncomeCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.municipalTaxes}:{' '}
            {formatAdminMoneyOrUnknown(financial.expenses?.municipalTaxesCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.schoolTaxes}:{' '}
            {formatAdminMoneyOrUnknown(financial.expenses?.schoolTaxesCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.insurance}:{' '}
            {formatAdminMoneyOrUnknown(financial.expenses?.insuranceCents, language, responseCopy.unknown)}
          </p>
          <p>
            {responseCopy.heatingElectricity}:{' '}
            {formatAdminMoneyOrUnknown(
              financial.expenses?.heatingElectricityCents,
              language,
              responseCopy.unknown,
            )}
          </p>
          <p>
            {responseCopy.otherExpenses}:{' '}
            {formatAdminMoneyOrUnknown(financial.expenses?.otherExpensesCents, language, responseCopy.unknown)}
          </p>
          {financial.otherExpensesDescription ? (
            <p>
              {responseCopy.otherExpensesDescription}: {financial.otherExpensesDescription}
            </p>
          ) : null}
        </div>
      ) : null}
      {item.intent === 'property_value' && item.schemaVersion === 3 && profile ? (
        <div className="mt-3 space-y-1">
          <p className="font-semibold text-slate-900">{responseCopy.building}</p>
          <p>
            {responseCopy.constructionYear}:{' '}
            {profile.constructionYearUnknown
              ? responseCopy.constructionYearUnknown
              : profile.constructionYear ?? responseCopy.unknown}
          </p>
          {profile.unitMix
            ? Object.entries(profile.unitMix)
                .filter(([, count]) => Number.isInteger(count) && count > 0)
                .map(([key, count]) => (
                  <p key={key}>
                    {responseCopy[UNIT_MIX_LABEL_KEYS[key]] || key}: {count}
                  </p>
                ))
            : null}
          {profile.declaredUnitTotal != null ? (
            <p>
              {responseCopy.declaredUnits}: {profile.declaredUnitTotal}
            </p>
          ) : null}
          {profile.importedNumberOfUnits != null ? (
            <p>
              {responseCopy.importedUnits}: {profile.importedNumberOfUnits}
            </p>
          ) : null}
          {profile.unitCountMismatchConfirmed ? (
            <p className="font-medium text-amber-800">{responseCopy.unitMismatch}</p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function CampaignMailingReadinessSummary({ summary, t }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-900">
        {t('admin.prospection.campaign.readiness.title')}
      </h2>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900">
            {t('admin.prospection.campaign.readiness.card.ready')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-emerald-950">{summary.readyTargetCount}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-950">
            {t('admin.prospection.campaign.readiness.card.partial')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-amber-950">{summary.partialTargetCount}</p>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-900">
            {t('admin.prospection.campaign.readiness.card.blocked')}
          </p>
          <p className="mt-1 text-2xl font-semibold text-rose-950">{summary.blockedTargetCount}</p>
        </div>
      </div>
      <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.readiness.metric.recipients')}
          </dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">{summary.recipientEntryCount}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.readiness.metric.mailableRecipients')}
          </dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">{summary.mailableRecipientCount}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.readiness.metric.incompleteRecipients')}
          </dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">{summary.incompleteRecipientCount}</dd>
        </div>
      </dl>
    </section>
  );
}

function CampaignTargetReadinessBlock({ mailingReadiness, t, compact = false }) {
  const status = mailingReadiness?.status;
  const reasons = formatCampaignTargetMailingReadinessReasons(mailingReadiness, t);
  const recipientsLine = formatCampaignTargetMailingReadinessRecipients(mailingReadiness, t);
  if (!status) return <span>{ADMIN_DATE_PLACEHOLDER}</span>;
  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      <span
        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${adminProspectCampaignMailingReadinessStatusBadgeClass(status)}`}
      >
        {adminProspectCampaignMailingReadinessStatusLabel(status, t)}
      </span>
      {recipientsLine ? <p className="text-xs text-slate-600">{recipientsLine}</p> : null}
      {reasons.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-slate-600">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CampaignTargetProspectAction({ prospectId, mailingReadiness, t }) {
  if (!prospectId) return ADMIN_DATE_PLACEHOLDER;
  const needsFix = campaignTargetNeedsProspectFixAction(mailingReadiness?.status);
  return (
    <div className="space-y-1">
      <Link
        to={`/admin/prospection/${prospectId}`}
        className="font-semibold text-indigo-700 hover:underline"
      >
        {needsFix
          ? t('admin.prospection.campaign.readiness.fixInProspect')
          : t('admin.prospection.campaign.targets.viewLive')}
      </Link>
      {needsFix ? (
        <p className="text-xs text-slate-500">{t('admin.prospection.campaign.readiness.fixInProspectHelp')}</p>
      ) : null}
    </div>
  );
}

function CampaignMailingsPrepareSection({
  campaign,
  preparing,
  prepareErrorKey,
  mailingPollTimedOut,
  canPrepare,
  canRetryMailingBuild,
  retryingMailingBuild,
  retryMailingBuildErrorKey,
  zeroMailable,
  onPrepare,
  onRetryMailingBuild,
  onRefreshStatus,
  t,
}) {
  const mailingBuildStatus = campaign.mailingBuildStatus || 'not_started';
  const prepareBusy = preparing || mailingBuildStatus === 'building';
  const expectedCount = Number.isInteger(campaign.mailingCountExpected)
    ? campaign.mailingCountExpected
    : ADMIN_DATE_PLACEHOLDER;
  const mailingCount = Number.isInteger(campaign.mailingCount)
    ? campaign.mailingCount
    : ADMIN_DATE_PLACEHOLDER;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" aria-labelledby="admin-campaign-mailings-prepare-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="admin-campaign-mailings-prepare-title" className="text-sm font-semibold text-slate-900">
            {t('admin.prospection.campaign.mailings.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.campaign.mailings.subtitle')}</p>
        </div>
        <span
          className={`inline-flex self-start rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${adminProspectCampaignMailingBuildStatusBadgeClass(mailingBuildStatus)}`}
        >
          {adminProspectCampaignMailingBuildStatusLabel(mailingBuildStatus, t)}
        </span>
      </div>

      {zeroMailable ? (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" role="status">
          {t('admin.prospection.campaign.mailings.zeroMailable')}
        </p>
      ) : null}

      {mailingBuildStatus === 'building' ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" role="status">
          <p className="font-semibold">{t('admin.prospection.campaign.mailings.buildingTitle')}</p>
          <p className="mt-1">{t('admin.prospection.campaign.mailings.buildingBody')}</p>
          <p className="mt-1">
            {t('admin.prospection.campaign.mailings.buildingProgress')
              .replace('{count}', String(mailingCount))
              .replace('{expected}', String(expectedCount))}
          </p>
          {mailingPollTimedOut ? (
            <div className="mt-3 space-y-2">
              <p className="font-medium">{t('admin.prospection.campaign.mailings.timeout')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onRefreshStatus}
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {t('admin.prospection.campaign.detail.refreshStatus')}
                </button>
                {canRetryMailingBuild ? (
                  <button
                    type="button"
                    onClick={onRetryMailingBuild}
                    disabled={retryingMailingBuild}
                    aria-busy={retryingMailingBuild}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  >
                    {retryingMailingBuild
                      ? t('admin.prospection.campaign.mailings.retryingBuild')
                      : t('admin.prospection.campaign.mailings.retryBuild')}
                  </button>
                ) : null}
              </div>
              {retryMailingBuildErrorKey ? (
                <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
                  {t(retryMailingBuildErrorKey)}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {mailingBuildStatus === 'failed' ? (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-950" role="alert">
          <p className="font-semibold">{t('admin.prospection.campaign.mailings.failed')}</p>
          {canRetryMailingBuild ? (
            <div className="mt-3 space-y-2">
              <button
                type="button"
                onClick={onRetryMailingBuild}
                disabled={retryingMailingBuild}
                aria-busy={retryingMailingBuild}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              >
                {retryingMailingBuild
                  ? t('admin.prospection.campaign.mailings.retryingBuild')
                  : t('admin.prospection.campaign.mailings.retryBuild')}
              </button>
              {retryMailingBuildErrorKey ? (
                <p className="rounded-lg border border-rose-200 bg-white/70 p-3 text-sm text-rose-900" role="alert">
                  {t(retryMailingBuildErrorKey)}
                </p>
              ) : null}
            </div>
          ) : null}
          {canPrepare ? (
            <button
              type="button"
              onClick={onPrepare}
              disabled={prepareBusy}
              aria-busy={prepareBusy}
              className="mt-3 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {t('admin.prospection.campaign.mailings.retryPrepare')}
            </button>
          ) : null}
        </div>
      ) : null}

      {canPrepare && mailingBuildStatus !== 'failed' ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={onPrepare}
            disabled={prepareBusy || !canPrepare}
            aria-label={t('admin.prospection.campaign.mailings.prepare')}
            aria-busy={prepareBusy}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {prepareBusy
              ? t('admin.prospection.campaign.mailings.preparing')
              : t('admin.prospection.campaign.mailings.prepare')}
          </button>
        </div>
      ) : null}

      {prepareErrorKey ? (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          {t(prepareErrorKey)}
        </p>
      ) : null}
    </section>
  );
}

function CampaignPdfLettersSection({
  campaign,
  mailings,
  mailingRows,
  mailingStatus,
  mailingErrorKind,
  letterActionErrorKey,
  generating,
  downloadingMailingId,
  letterPollTimedOut,
  canGenerate,
  generateDisabled,
  generateLabel,
  onGenerate,
  onDownload,
  onViewResponses,
  responsePanel,
  responsePanelElementRef,
  onCloseResponses,
  onMailingsPrevious,
  onMailingsNext,
  mailingTokenStackLength,
  mailingNextPageToken,
  language,
  t,
}) {
  const summary = campaign?.mailingReadinessSummary;
  const mailableCount = Number.isInteger(summary?.mailableRecipientCount)
    ? summary.mailableRecipientCount
    : campaign.mailingCount ?? ADMIN_DATE_PLACEHOLDER;
  const blockedCount = Number.isInteger(summary?.incompleteRecipientCount)
    ? summary.incompleteRecipientCount
    : ADMIN_DATE_PLACEHOLDER;
  const counts = countMailingLetterStatuses(mailings);
  const generatedCount = Number.isInteger(campaign.letterCountGenerated)
    ? campaign.letterCountGenerated
    : counts.generated;
  const expectedCount = Number.isInteger(campaign.letterCountExpected)
    ? campaign.letterCountExpected
    : campaign.mailingCount;
  const pendingCount =
    Number.isInteger(expectedCount)
      ? Math.max(0, expectedCount - generatedCount - counts.failed)
      : counts.pending;
  const letterBuildStatus = campaign.letterBuildStatus || 'not_started';
  const generateBusy = generating || letterBuildStatus === 'building';
  const responseCopy = adminProspectPublicResponseCopy(language);

  return (
    <section className="space-y-3" aria-labelledby="admin-campaign-pdf-letters-title">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 id="admin-campaign-pdf-letters-title" className="text-sm font-semibold text-slate-900">
              {t('admin.prospection.campaign.letters.title')}
            </h2>
            <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.campaign.letters.subtitle')}</p>
          </div>
          <span
            className={`inline-flex self-start rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${adminProspectCampaignLetterBuildStatusBadgeClass(letterBuildStatus)}`}
          >
            {adminProspectCampaignLetterBuildStatusLabel(letterBuildStatus, t)}
          </span>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.letters.mailable')}
            </dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{mailableCount}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.letters.blocked')}
            </dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{blockedCount}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.letters.generated')}
            </dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{generatedCount}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.letters.failed')}
            </dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{counts.failed}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.letters.pending')}
            </dt>
            <dd className="mt-1 text-sm font-medium text-slate-900">{pendingCount}</dd>
          </div>
        </dl>

        {letterBuildStatus === 'building' ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" role="status">
            <p className="font-semibold">{t('admin.prospection.campaign.letters.buildingTitle')}</p>
            <p className="mt-1">{t('admin.prospection.campaign.letters.buildingBody')}</p>
            <p className="mt-1">
              {t('admin.prospection.campaign.letters.buildingProgress')
                .replace('{generated}', String(generatedCount))
                .replace('{expected}', String(expectedCount ?? ADMIN_DATE_PLACEHOLDER))}
            </p>
            {letterPollTimedOut ? (
              <p className="mt-2 font-medium">{t('admin.prospection.campaign.letters.timeout')}</p>
            ) : null}
          </div>
        ) : null}

        {letterBuildStatus === 'failed' ? (
          <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-950" role="alert">
            {t('admin.prospection.campaign.letters.partialFailure')}
          </p>
        ) : null}

        {campaign.mailingBuildStatus !== 'ready' ? (
          <p className="mt-4 text-sm text-slate-600">{t('admin.prospection.campaign.letters.notReady')}</p>
        ) : null}

        {campaign.mailingBuildStatus === 'ready' && Number(campaign.mailingCount) <= 0 ? (
          <p className="mt-4 text-sm text-slate-600">{t('admin.prospection.campaign.letters.noMailable')}</p>
        ) : null}

        {canGenerate || generateBusy ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={onGenerate}
              disabled={generateDisabled}
              aria-label={generateLabel}
              aria-busy={generateBusy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {generateBusy ? t('admin.prospection.campaign.letters.generating') : generateLabel}
            </button>
          </div>
        ) : null}

        {letterActionErrorKey ? (
          <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
            {t(letterActionErrorKey)}
          </p>
        ) : null}
      </div>

      {mailingStatus === 'loading' ? (
        <p className="text-sm text-slate-500">{t('admin.prospection.campaign.letters.loading')}</p>
      ) : null}
      {mailingStatus === 'paging' ? (
        <p className="text-sm text-slate-500">{t('admin.prospection.campaign.letters.paging')}</p>
      ) : null}
      {mailingStatus === 'error' ? (
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
          {t(adminProspectCampaignErrorKey(mailingErrorKind))}
        </p>
      ) : null}
      {mailingStatus === 'empty' && mailingRows.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.prospection.campaign.letters.empty')}
        </p>
      ) : null}

      {mailingRows.length > 0 ? (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.letters.recipient')}</th>
                  <th className="min-w-[14rem] whitespace-nowrap px-3 py-2">
                    {t('admin.prospection.campaign.letters.property')}
                  </th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.letters.letterStatus')}</th>
                  <th className="px-3 py-2">{responseCopy.column}</th>
                  <th className="px-3 py-2">{t('admin.prospection.campaign.targets.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mailingRows.map((row) => {
                  const downloadable = isCampaignLetterDownloadable(row, campaign);
                  const downloading = downloadingMailingId === row.mailingId;
                  const badges = listMailingReceivedIntentBadges(row.publicResponse);
                  return (
                    <tr key={row.key} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-900">{formatCampaignLetterRecipientName(row.recipient)}</p>
                        <p className="text-xs text-slate-600">{formatCampaignLetterRecipientMailing(row.recipient)}</p>
                        {row.kind === 'blocked' ? (
                          <p className="mt-1 text-xs font-semibold text-rose-800">
                            {t('admin.prospection.campaign.letters.blockedLabel')}
                          </p>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {formatCampaignTargetAddress(row) || ADMIN_DATE_PLACEHOLDER}
                      </td>
                      <td className="px-3 py-2">
                        {row.kind === 'blocked'
                          ? t('admin.prospection.campaign.letters.blockedLabel')
                          : adminProspectCampaignMailingLetterStatusLabel(row.letterStatus, t)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {badges.map((badge) => (
                            <span
                              key={badge.intent}
                              className="inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-900 ring-1 ring-teal-200"
                              title={badge.submittedAt || undefined}
                            >
                              {adminProspectPublicResponseIntentLabel(badge.intent, language)}
                            </span>
                          ))}
                          {badges.length === 0 ? ADMIN_DATE_PLACEHOLDER : null}
                        </div>
                        {row.kind === 'mailing' && badges.length > 0 ? (
                          <button
                            type="button"
                            className="mt-2 text-xs font-semibold text-indigo-700 underline"
                            onClick={() => onViewResponses(row)}
                          >
                            {responseCopy.viewDetails}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        {downloadable ? (
                          <button
                            type="button"
                            onClick={() => onDownload(row)}
                            disabled={generateBusy || downloading}
                            aria-label={t('admin.prospection.campaign.letters.download')}
                            aria-busy={downloading}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                          >
                            {downloading
                              ? t('admin.prospection.campaign.letters.downloading')
                              : t('admin.prospection.campaign.letters.download')}
                          </button>
                        ) : ADMIN_DATE_PLACEHOLDER}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {responsePanel && responsePanel.status !== 'closed' ? (
            <div
              ref={responsePanelElementRef}
              tabIndex={-1}
              className="mt-4 rounded-xl border border-slate-200 bg-white p-4 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              role="region"
              aria-label={responseCopy.panelLabel}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-900">{responseCopy.panelTitle}</h3>
                <button type="button" className="text-sm font-semibold text-slate-700 underline" onClick={onCloseResponses}>
                  {responseCopy.close}
                </button>
              </div>
              {responsePanel.status === 'loading' ? (
                <p className="text-sm text-slate-600">{responseCopy.loading}</p>
              ) : null}
              {responsePanel.status === 'error' ? (
                <p className="text-sm text-rose-800" role="alert">
                  {responseCopy.error}
                </p>
              ) : null}
              {responsePanel.status === 'ready' ? (
                <ul className="space-y-3">
                  {(responsePanel.responses || []).map((item) => (
                    <AdminPublicResponseDetailItem
                      key={item.intent}
                      item={item}
                      language={language}
                      responseCopy={responseCopy}
                    />
                  ))}
                  {(responsePanel.responses || []).length === 0 ? (
                    <li className="text-sm text-slate-600">{responseCopy.empty}</li>
                  ) : null}
                </ul>
              ) : null}
            </div>
          ) : null}

          <ul className="space-y-3 md:hidden">
            {mailingRows.map((row) => {
              const downloadable = isCampaignLetterDownloadable(row, campaign);
              const downloading = downloadingMailingId === row.mailingId;
              const badges = listMailingReceivedIntentBadges(row.publicResponse);
              return (
                <li key={row.key} className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="font-semibold text-slate-900">{formatCampaignLetterRecipientName(row.recipient)}</p>
                  <p className="mt-1 text-sm text-slate-600">{formatCampaignLetterRecipientMailing(row.recipient)}</p>
                  <p className="mt-2 text-sm text-slate-600">
                    {formatCampaignTargetAddress(row) || ADMIN_DATE_PLACEHOLDER}
                  </p>
                  {row.kind === 'blocked' ? (
                    <p className="mt-2 text-xs font-semibold text-rose-800">
                      {t('admin.prospection.campaign.letters.blockedLabel')}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-slate-700">
                      {adminProspectCampaignMailingLetterStatusLabel(row.letterStatus, t)}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {badges.map((badge) => (
                      <span
                        key={badge.intent}
                        className="inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-900 ring-1 ring-teal-200"
                        title={badge.submittedAt || undefined}
                      >
                        {adminProspectPublicResponseIntentLabel(badge.intent, language)}
                        {badge.submittedAt ? (
                          <span className="ml-1 font-normal text-teal-800/80">
                            {badge.submittedAt}
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                  {row.kind === 'mailing' && badges.length > 0 ? (
                    <button
                      type="button"
                      className="mt-2 text-xs font-semibold text-indigo-700 underline"
                      onClick={() => onViewResponses(row)}
                    >
                      {responseCopy.viewDetails}
                    </button>
                  ) : null}
                  {downloadable ? (
                    <button
                      type="button"
                      onClick={() => onDownload(row)}
                      disabled={generateBusy || downloading}
                      aria-label={t('admin.prospection.campaign.letters.download')}
                      aria-busy={downloading}
                      className="mt-3 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {downloading
                        ? t('admin.prospection.campaign.letters.downloading')
                        : t('admin.prospection.campaign.letters.download')}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {mailingTokenStackLength > 0 || mailingNextPageToken ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onMailingsPrevious}
                disabled={mailingTokenStackLength === 0 || mailingStatus === 'paging'}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {t('admin.prospection.campaign.list.prev')}
              </button>
              <button
                type="button"
                onClick={onMailingsNext}
                disabled={!mailingNextPageToken || mailingStatus === 'paging'}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {t('admin.prospection.campaign.list.next')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function CampaignLetterArchiveSection({
  campaign,
  requesting,
  retrying,
  archiveActionErrorKey,
  archivePollTimedOut,
  downloading,
  canRequest,
  canRetry,
  onRequest,
  onRetry,
  onDownload,
  onRefreshStatus,
  t,
}) {
  const status = campaign?.letterArchiveStatus || 'not_started';
  const busy = requesting || retrying || status === 'building';
  const expected = Number.isInteger(campaign?.letterArchiveCountExpected)
    ? campaign.letterArchiveCountExpected
    : null;
  const generated = Number.isInteger(campaign?.letterArchiveCountGenerated)
    ? campaign.letterArchiveCountGenerated
    : 0;
  const percent = Number.isInteger(campaign?.letterArchiveProgressPercent)
    ? campaign.letterArchiveProgressPercent
    : null;
  const downloadable = isCampaignLetterArchiveDownloadable(campaign);
  const requestLabel =
    status === 'ready'
      ? t('admin.prospection.campaign.letters.archive.rebuild')
      : t('admin.prospection.campaign.letters.archive.request');

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" aria-labelledby="admin-campaign-letter-archive-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="admin-campaign-letter-archive-title" className="text-sm font-semibold text-slate-900">
            {t('admin.prospection.campaign.letters.archive.title')}
          </h2>
          <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.campaign.letters.archive.subtitle')}</p>
        </div>
        <span
          className={`inline-flex self-start rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${adminProspectCampaignLetterArchiveStatusBadgeClass(status)}`}
        >
          {adminProspectCampaignLetterArchiveStatusLabel(status, t)}
        </span>
      </div>

      {status === 'building' ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950" role="status">
          <p className="font-semibold">
            {t('admin.prospection.campaign.letters.archive.buildingProgress')
              .replace('{generated}', String(generated))
              .replace('{expected}', String(expected ?? ADMIN_DATE_PLACEHOLDER))
              .replace('{percent}', String(percent ?? 0))}
          </p>
          {archivePollTimedOut ? (
            <div className="mt-2 space-y-2">
              <p className="font-medium">{t('admin.prospection.campaign.letters.archive.timeout')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onRefreshStatus}
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {t('admin.prospection.campaign.detail.refreshStatus')}
                </button>
                {canRetry ? (
                  <button
                    type="button"
                    onClick={onRetry}
                    disabled={busy}
                    aria-busy={retrying}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  >
                    {retrying
                      ? t('admin.prospection.campaign.letters.archive.retrying')
                      : t('admin.prospection.campaign.letters.archive.retry')}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {status === 'failed' ? (
        <div className="mt-4 space-y-2">
          <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-950" role="alert">
            {t('admin.prospection.campaign.letters.archive.failedBody')}
          </p>
          {canRetry ? (
            <button
              type="button"
              onClick={onRetry}
              disabled={busy}
              aria-busy={retrying}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {retrying
                ? t('admin.prospection.campaign.letters.archive.retrying')
                : t('admin.prospection.campaign.letters.archive.retry')}
            </button>
          ) : null}
        </div>
      ) : null}

      {!canRequest && !canRetry && status !== 'building' && status !== 'ready' ? (
        <p className="mt-4 text-sm text-slate-600">{t('admin.prospection.campaign.letters.archive.notReady')}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {canRequest || (status === 'building' && requesting) ? (
          <button
            type="button"
            onClick={onRequest}
            disabled={!canRequest || busy}
            aria-busy={requesting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          >
            {requesting || status === 'building'
              ? t('admin.prospection.campaign.letters.archive.requesting')
              : requestLabel}
          </button>
        ) : null}
        {downloadable ? (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            aria-busy={downloading}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {downloading
              ? t('admin.prospection.campaign.letters.archive.downloading')
              : t('admin.prospection.campaign.letters.archive.download')}
          </button>
        ) : null}
      </div>

      {archiveActionErrorKey ? (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          {t(archiveActionErrorKey)}
        </p>
      ) : null}
    </section>
  );
}


export default function AdminProspectCampaignDetailPage() {
  const { campaignId } = useParams();
  const { t, language } = useLanguage();
  const [campaign, setCampaign] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const [targets, setTargets] = useState([]);
  const [targetsStatus, setTargetsStatus] = useState('idle');
  const [targetsErrorKind, setTargetsErrorKind] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);

  const [mailings, setMailings] = useState([]);
  const [mailingsStatus, setMailingsStatus] = useState('idle');
  const [mailingsErrorKind, setMailingsErrorKind] = useState(null);
  const [mailingsNextPageToken, setMailingsNextPageToken] = useState(null);
  const [mailingsTokenStack, setMailingsTokenStack] = useState([]);
  const [mailingsCurrentToken, setMailingsCurrentToken] = useState(null);

  const [generatingLetters, setGeneratingLetters] = useState(false);
  const [letterActionErrorKey, setLetterActionErrorKey] = useState(null);
  const [letterPollTimedOut, setLetterPollTimedOut] = useState(false);
  const [downloadingMailingId, setDownloadingMailingId] = useState(null);
  const [responsePanel, setResponsePanel] = useState(() => createClosedAdminPublicResponsePanel());
  const responseRequestTrackerRef = useRef(createAdminPublicResponseRequestTracker());
  const responsePanelRef = useRef(responsePanel);
  responsePanelRef.current = responsePanel;
  const responsePanelElementRef = useRef(null);
  const pendingResponsePanelRevealOwnerRef = useRef(null);

  const [requestingArchive, setRequestingArchive] = useState(false);
  const [retryingArchive, setRetryingArchive] = useState(false);
  const [archiveActionErrorKey, setArchiveActionErrorKey] = useState(null);
  const [archivePollTimedOut, setArchivePollTimedOut] = useState(false);
  const [downloadingArchive, setDownloadingArchive] = useState(false);

  const [preparingMailings, setPreparingMailings] = useState(false);
  const [prepareActionErrorKey, setPrepareActionErrorKey] = useState(null);
  const [mailingPollTimedOut, setMailingPollTimedOut] = useState(false);
  const [retryingBuild, setRetryingBuild] = useState(false);
  const [retryBuildErrorKey, setRetryBuildErrorKey] = useState(null);
  const [retryingMailingBuild, setRetryingMailingBuild] = useState(false);
  const [retryMailingBuildErrorKey, setRetryMailingBuildErrorKey] = useState(null);

  const pollStartedAtRef = useRef(0);
  const letterPollStartedAtRef = useRef(0);
  const mailingPollStartedAtRef = useRef(0);
  const archivePollStartedAtRef = useRef(0);
  const targetsLoadedForRef = useRef(null);
  const targetsTokenStackRef = useRef([]);
  const mailingsLoadedForRef = useRef(null);
  const mailingsTokenStackRef = useRef([]);
  const mailingsCurrentTokenRef = useRef(null);
  const generateLockRef = useRef(false);
  const prepareLockRef = useRef(false);
  const retryBuildLockRef = useRef(false);
  const retryMailingBuildLockRef = useRef(false);
  const downloadLockRef = useRef(false);
  const requestArchiveLockRef = useRef(false);
  const retryArchiveLockRef = useRef(false);
  const downloadArchiveLockRef = useRef(false);
  const letterPollInFlightRef = useRef(false);
  const mailingPollInFlightRef = useRef(false);
  const archivePollInFlightRef = useRef(false);
  const prepareAttemptRef = useRef(createAdminProspectCampaignOperationAttempt());
  const retryBuildAttemptRef = useRef(createAdminProspectCampaignOperationAttempt());
  const retryMailingBuildAttemptRef = useRef(createAdminProspectCampaignOperationAttempt());
  const requestArchiveAttemptRef = useRef(createAdminProspectCampaignOperationAttempt());
  const retryArchiveAttemptRef = useRef(createAdminProspectCampaignOperationAttempt());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    responseRequestTrackerRef.current.invalidate();
    pendingResponsePanelRevealOwnerRef.current = null;
    const closed = createClosedAdminPublicResponsePanel();
    setResponsePanel(closed);
    responsePanelRef.current = closed;
  }, [campaignId]);

  useEffect(() => {
    const owner = pendingResponsePanelRevealOwnerRef.current;
    if (
      !shouldRevealAdminPublicResponsePanel({
        owner,
        tracker: responseRequestTrackerRef.current,
        mounted: mountedRef.current,
        panel: responsePanel,
      })
    ) {
      return;
    }
    pendingResponsePanelRevealOwnerRef.current = null;
    revealAdminPublicResponsePanelElement(responsePanelElementRef.current);
  }, [responsePanel]);

  const loadCampaign = useCallback(async () => {
    if (!campaignId) {
      setStatus('error');
      setErrorKind('not-found');
      return null;
    }
    try {
      const data = await getAdminProspectCampaign(campaignId);
      if (!mountedRef.current) return data;
      setCampaign(data);
      setErrorKind(null);
      setStatus('ready');
      return data;
    } catch (err) {
      if (!mountedRef.current) return null;
      setCampaign(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
      return null;
    }
  }, [campaignId]);

  useEffect(() => {
    setStatus('loading');
    setPollTimedOut(false);
    pollStartedAtRef.current = Date.now();
    targetsLoadedForRef.current = null;
    setTargets([]);
    setTargetsStatus('idle');
    setTokenStack([]);
    targetsTokenStackRef.current = [];
    setCurrentToken(null);
    setNextPageToken(null);
    setMailings([]);
    setMailingsStatus('idle');
    setMailingsErrorKind(null);
    setMailingsNextPageToken(null);
    setMailingsTokenStack([]);
    mailingsTokenStackRef.current = [];
    setMailingsCurrentToken(null);
    mailingsCurrentTokenRef.current = null;
    mailingsLoadedForRef.current = null;
    setGeneratingLetters(false);
    setLetterActionErrorKey(null);
    setLetterPollTimedOut(false);
    letterPollStartedAtRef.current = 0;
    setPreparingMailings(false);
    setPrepareActionErrorKey(null);
    setMailingPollTimedOut(false);
    mailingPollStartedAtRef.current = 0;
    setRetryingBuild(false);
    setRetryBuildErrorKey(null);
    setRequestingArchive(false);
    setArchiveActionErrorKey(null);
    setArchivePollTimedOut(false);
    archivePollStartedAtRef.current = 0;
    setDownloadingArchive(false);
    generateLockRef.current = false;
    prepareLockRef.current = false;
    retryBuildLockRef.current = false;
    downloadLockRef.current = false;
    requestArchiveLockRef.current = false;
    downloadArchiveLockRef.current = false;
    letterPollInFlightRef.current = false;
    mailingPollInFlightRef.current = false;
    archivePollInFlightRef.current = false;
    prepareAttemptRef.current.reset();
    retryBuildAttemptRef.current.reset();
    requestArchiveAttemptRef.current.reset();
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (status !== 'ready') return undefined;
    if (campaign?.buildStatus !== 'building') return undefined;
    if (pollTimedOut) return undefined;

    let cancelled = false;
    let timerId = 0;

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - pollStartedAtRef.current >= ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MAX_MS) {
        setPollTimedOut(true);
        return;
      }
      const data = await loadCampaign();
      if (cancelled) return;
      if (data?.buildStatus === 'building') {
        timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS);
      }
    };

    timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_BUILD_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [status, campaign?.buildStatus, pollTimedOut, loadCampaign]);

  const loadMailings = async (pageToken, { paging = false, nextStack, quiet = false } = {}) => {
    if (!campaignId) return;
    if (!quiet) {
      setMailingsStatus(paging ? 'paging' : 'loading');
      setMailingsErrorKind(null);
    }
    try {
      const data = await listAdminProspectCampaignMailings(
        buildListAdminProspectCampaignMailingsRequest({
          campaignId,
          pageSize: ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE,
          pageToken: pageToken || null,
        }),
      );
      if (!mountedRef.current) return;
      const rows = Array.isArray(data?.mailings) ? data.mailings : [];
      setMailings(rows);
      setMailingsNextPageToken(data?.nextPageToken || null);
      setMailingsCurrentToken(pageToken || null);
      mailingsCurrentTokenRef.current = pageToken || null;
      if (Array.isArray(nextStack)) {
        setMailingsTokenStack(nextStack);
        mailingsTokenStackRef.current = nextStack;
      } else if (!paging && !quiet) {
        setMailingsTokenStack([]);
        mailingsTokenStackRef.current = [];
      }
      setMailingsStatus(rows.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      if (!mountedRef.current) return;
      if (quiet) return;
      setMailings([]);
      setMailingsNextPageToken(null);
      setMailingsErrorKind(classifyAdminCallableError(err));
      setMailingsStatus('error');
    }
  };

  useEffect(() => {
    if (campaign?.mailingBuildStatus !== 'ready') return;
    if (!campaignId) return;
    if (mailingsLoadedForRef.current === campaignId) return;
    mailingsLoadedForRef.current = campaignId;
    loadMailings(undefined);
  }, [campaign?.mailingBuildStatus, campaignId]);

  useEffect(() => {
    if (status !== 'ready') return undefined;
    if (campaign?.mailingBuildStatus !== 'building') return undefined;
    if (mailingPollTimedOut) return undefined;
    if (!mailingPollStartedAtRef.current) {
      mailingPollStartedAtRef.current = Date.now();
    }

    let cancelled = false;
    let timerId = 0;

    const tick = async () => {
      if (cancelled || mailingPollInFlightRef.current) return;
      if (Date.now() - mailingPollStartedAtRef.current >= ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MAX_MS) {
        setMailingPollTimedOut(true);
        return;
      }
      mailingPollInFlightRef.current = true;
      try {
        const data = await loadCampaign();
        if (cancelled) return;
        if (data?.mailingBuildStatus === 'ready') {
          mailingsLoadedForRef.current = null;
          await loadMailings(undefined, { quiet: true });
          prepareAttemptRef.current.reset();
        } else if (data?.mailingBuildStatus === 'failed') {
          prepareAttemptRef.current.reset();
        } else if (data?.mailingBuildStatus === 'building') {
          timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MS);
        }
      } finally {
        mailingPollInFlightRef.current = false;
      }
    };

    timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_MAILING_BUILD_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [status, campaign?.mailingBuildStatus, mailingPollTimedOut, loadCampaign]);

  useEffect(() => {
    if (status !== 'ready') return undefined;
    if (campaign?.letterBuildStatus !== 'building') return undefined;
    if (letterPollTimedOut) return undefined;
    if (!letterPollStartedAtRef.current) {
      letterPollStartedAtRef.current = Date.now();
    }

    let cancelled = false;
    let timerId = 0;

    const tick = async () => {
      if (cancelled || letterPollInFlightRef.current) return;
      if (Date.now() - letterPollStartedAtRef.current >= ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MAX_MS) {
        setLetterPollTimedOut(true);
        return;
      }
      letterPollInFlightRef.current = true;
      try {
        const data = await loadCampaign();
        if (cancelled) return;
        if (campaign?.mailingBuildStatus === 'ready') {
          await loadMailings(mailingsCurrentTokenRef.current, { quiet: true });
        }
        if (cancelled) return;
        if (data?.letterBuildStatus === 'building') {
          timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MS);
        }
      } finally {
        letterPollInFlightRef.current = false;
      }
    };

    timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_LETTER_BUILD_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [status, campaign?.letterBuildStatus, letterPollTimedOut, loadCampaign, campaign?.mailingBuildStatus]);

  useEffect(() => {
    if (status !== 'ready') return undefined;
    if (campaign?.letterArchiveStatus !== 'building') return undefined;
    if (archivePollTimedOut) return undefined;
    if (!archivePollStartedAtRef.current) {
      archivePollStartedAtRef.current = Date.now();
    }

    let cancelled = false;
    let timerId = 0;

    const tick = async () => {
      if (cancelled || archivePollInFlightRef.current) return;
      if (Date.now() - archivePollStartedAtRef.current >= ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MAX_MS) {
        setArchivePollTimedOut(true);
        return;
      }
      archivePollInFlightRef.current = true;
      try {
        const data = await loadCampaign();
        if (cancelled) return;
        if (data?.letterArchiveStatus === 'building') {
          timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MS);
        }
      } finally {
        archivePollInFlightRef.current = false;
      }
    };

    timerId = window.setTimeout(tick, ADMIN_PROSPECT_CAMPAIGN_LETTER_ARCHIVE_BUILD_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [status, campaign?.letterArchiveStatus, archivePollTimedOut, loadCampaign]);

  const loadTargets = async (pageToken, { paging = false, nextStack } = {}) => {
    if (!campaignId) return;
    setTargetsStatus(paging ? 'paging' : 'loading');
    setTargetsErrorKind(null);
    try {
      const data = await listAdminProspectCampaignTargets(
        buildListAdminProspectCampaignTargetsRequest({
          campaignId,
          pageSize: ADMIN_PROSPECT_CAMPAIGN_TARGETS_PAGE_SIZE,
          pageToken: pageToken || null,
        }),
      );
      const rows = Array.isArray(data?.targets) ? data.targets : [];
      setTargets(rows);
      setNextPageToken(data?.nextPageToken || null);
      setCurrentToken(pageToken || null);
      if (Array.isArray(nextStack)) {
        setTokenStack(nextStack);
        targetsTokenStackRef.current = nextStack;
      } else if (!paging) {
        setTokenStack([]);
        targetsTokenStackRef.current = [];
      }
      setTargetsStatus(rows.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      setTargets([]);
      setNextPageToken(null);
      setTargetsErrorKind(classifyAdminCallableError(err));
      setTargetsStatus('error');
    }
  };

  useEffect(() => {
    if (campaign?.buildStatus !== 'ready') return;
    if (!campaignId) return;
    if (targetsLoadedForRef.current === campaignId) return;
    targetsLoadedForRef.current = campaignId;
    loadTargets(undefined);
  }, [campaign?.buildStatus, campaignId]);

  const handleTargetsNext = () => {
    if (!nextPageToken) return;
    loadTargets(nextPageToken, {
      paging: true,
      nextStack: [...targetsTokenStackRef.current, currentToken],
    });
  };

  const handleTargetsPrevious = () => {
    if (targetsTokenStackRef.current.length === 0) return;
    const stack = [...targetsTokenStackRef.current];
    const prev = stack.pop();
    loadTargets(prev, { paging: true, nextStack: stack });
  };

  const handleMailingsNext = () => {
    if (!mailingsNextPageToken) return;
    loadMailings(mailingsNextPageToken, {
      paging: true,
      nextStack: [...mailingsTokenStackRef.current, mailingsCurrentToken],
    });
  };

  const handleMailingsPrevious = () => {
    if (mailingsTokenStackRef.current.length === 0) return;
    const stack = [...mailingsTokenStackRef.current];
    const prev = stack.pop();
    loadMailings(prev, { paging: true, nextStack: stack });
  };

  const handleRefreshCampaignStatus = async () => {
    setRetryBuildErrorKey(null);
    setRetryMailingBuildErrorKey(null);
    setPrepareActionErrorKey(null);
    await loadCampaign();
  };

  const handleRetryBuild = async () => {
    if (!campaignId || !canRetryAdminProspectCampaignBuild(campaign)) return;
    if (retryBuildLockRef.current || retryingBuild) return;
    retryBuildLockRef.current = true;
    setRetryingBuild(true);
    setRetryBuildErrorKey(null);
    try {
      const operationId = retryBuildAttemptRef.current.resolveOperationId();
      const data = await retryAdminProspectCampaignBuild(
        buildRetryAdminProspectCampaignBuildRequest({ campaignId, operationId }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              buildStatus: data.buildStatus ?? prev.buildStatus,
              buildRecoverable: data.buildRecoverable === true,
              ...(data.failureCode ? { failureCode: data.failureCode } : {}),
            }
          : prev,
      );
      setPollTimedOut(false);
      pollStartedAtRef.current = Date.now();
      if (data.buildStatus === 'building' || data.recovered || data.duplicate) {
        retryBuildAttemptRef.current.reset();
      }
      await loadCampaign();
    } catch (err) {
      if (!mountedRef.current) return;
      setRetryBuildErrorKey(adminProspectCampaignErrorKey(classifyAdminCallableError(err)));
    } finally {
      retryBuildLockRef.current = false;
      if (mountedRef.current) setRetryingBuild(false);
    }
  };

  const handleRetryMailingBuild = async () => {
    if (!campaignId || !canRetryAdminProspectCampaignMailingBuild(campaign)) return;
    if (retryMailingBuildLockRef.current || retryingMailingBuild) return;
    retryMailingBuildLockRef.current = true;
    setRetryingMailingBuild(true);
    setRetryMailingBuildErrorKey(null);
    try {
      const operationId = retryMailingBuildAttemptRef.current.resolveOperationId();
      const data = await retryAdminProspectCampaignMailingBuild(
        buildRetryAdminProspectCampaignMailingBuildRequest({ campaignId, operationId }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              mailingBuildStatus: data.mailingBuildStatus ?? prev.mailingBuildStatus,
              mailingBuildRecoverable: data.mailingBuildRecoverable === true,
              ...(data.failureCode ? { mailingFailureCode: data.failureCode } : {}),
            }
          : prev,
      );
      setMailingPollTimedOut(false);
      mailingPollStartedAtRef.current = Date.now();
      if (data.mailingBuildStatus === 'building' || data.recovered || data.duplicate) {
        retryMailingBuildAttemptRef.current.reset();
      }
      await loadCampaign();
    } catch (err) {
      if (!mountedRef.current) return;
      setRetryMailingBuildErrorKey(adminProspectCampaignErrorKey(classifyAdminCallableError(err)));
    } finally {
      retryMailingBuildLockRef.current = false;
      if (mountedRef.current) setRetryingMailingBuild(false);
    }
  };

  const handlePrepareMailings = async () => {
    if (!campaignId || !canPrepareAdminProspectCampaignMailings(campaign)) return;
    if (prepareLockRef.current || preparingMailings) return;
    if (campaign.mailingBuildStatus === 'failed') {
      prepareAttemptRef.current.reset();
    }
    prepareLockRef.current = true;
    setPreparingMailings(true);
    setPrepareActionErrorKey(null);
    setMailingPollTimedOut(false);
    mailingPollStartedAtRef.current = Date.now();
    try {
      const operationId = prepareAttemptRef.current.resolveOperationId();
      const data = await prepareAdminProspectCampaignMailings(
        buildPrepareAdminProspectCampaignMailingsRequest({ campaignId, operationId }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              mailingBuildStatus: data.mailingBuildStatus,
              mailingCountExpected: data.mailingCountExpected,
              mailingCount: data.mailingCount,
            }
          : prev,
      );
      if (data.mailingBuildStatus === 'building') {
        setMailingPollTimedOut(false);
      }
      if (data.mailingBuildStatus === 'ready') {
        prepareAttemptRef.current.reset();
        mailingsLoadedForRef.current = null;
        await loadMailings(undefined);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setPrepareActionErrorKey(adminProspectCampaignErrorKey(classifyAdminCallableError(err)));
    } finally {
      prepareLockRef.current = false;
      if (mountedRef.current) setPreparingMailings(false);
    }
  };

  const handleGenerateLetters = async () => {
    if (!campaignId || !canGenerateAdminProspectCampaignLetters(campaign)) return;
    if (generateLockRef.current || generatingLetters) return;
    generateLockRef.current = true;
    setGeneratingLetters(true);
    setLetterActionErrorKey(null);
    setLetterPollTimedOut(false);
    letterPollStartedAtRef.current = Date.now();
    try {
      const data = await generateAdminProspectCampaignLetters(
        buildGenerateAdminProspectCampaignLettersRequest({
          campaignId,
          operationId: generateAdminProspectCampaignOperationId(),
        }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              letterBuildStatus: data.letterBuildStatus,
              letterCountExpected: data.letterCountExpected,
              letterCountGenerated: data.letterCountGenerated,
            }
          : prev,
      );
      if (data.letterBuildStatus === 'building') {
        setLetterPollTimedOut(false);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setLetterActionErrorKey(adminProspectCampaignLetterErrorKey(err));
    } finally {
      generateLockRef.current = false;
      if (mountedRef.current) setGeneratingLetters(false);
    }
  };

  const handleDownloadLetter = async (row) => {
    if (!row?.mailingId || !campaignId) return;
    if (!isCampaignLetterDownloadable(row, campaign)) return;
    if (downloadLockRef.current || generatingLetters || campaign?.letterBuildStatus === 'building') return;
    setDownloadingMailingId(row.mailingId);
    setLetterActionErrorKey(null);
    try {
      const outcome = await runAdminProspectCampaignLetterDownloadAttempt({
        inFlightRef: downloadLockRef,
        request: buildGetAdminProspectCampaignLetterDownloadUrlRequest({
          campaignId,
          mailingId: row.mailingId,
        }),
        requestDownloadUrl: getAdminProspectCampaignLetterDownloadUrl,
      });
      if (!mountedRef.current) return;
      if (outcome.outcome === 'in_flight') return;
      if (outcome.outcome === 'popup_blocked') {
        setLetterActionErrorKey('admin.prospection.campaign.letters.popupBlocked');
      } else if (outcome.outcome === 'expired') {
        setLetterActionErrorKey('admin.prospection.campaign.letters.downloadExpired');
      } else if (outcome.outcome === 'error') {
        setLetterActionErrorKey(
          outcome.error
            ? adminProspectCampaignLetterErrorKey(outcome.error)
            : 'admin.prospection.campaign.letters.downloadError',
        );
      }
    } finally {
      if (mountedRef.current) setDownloadingMailingId(null);
    }
  };

  const handleViewResponses = async (row) => {
    if (!row?.mailingId || !campaignId) return;
    const tracker = responseRequestTrackerRef.current;
    const owner = tracker.begin({ campaignId, mailingId: row.mailingId });
    const loadingPanel = createLoadingAdminPublicResponsePanel({
      campaignId,
      mailingId: row.mailingId,
    });
    pendingResponsePanelRevealOwnerRef.current = owner;
    setResponsePanel(loadingPanel);
    responsePanelRef.current = loadingPanel;
    try {
      const data = await getAdminProspectCampaignMailingPublicResponse({
        campaignId,
        mailingId: row.mailingId,
      });
      if (
        !shouldApplyAdminPublicResponseResult({
          owner,
          tracker,
          mounted: mountedRef.current,
          panel: responsePanelRef.current,
          activeCampaignId: campaignId,
        })
      ) {
        return;
      }
      const readyPanel = {
        status: 'ready',
        campaignId,
        mailingId: row.mailingId,
        responses: data.responses,
      };
      setResponsePanel(readyPanel);
      responsePanelRef.current = readyPanel;
    } catch {
      if (
        !shouldApplyAdminPublicResponseResult({
          owner,
          tracker,
          mounted: mountedRef.current,
          panel: responsePanelRef.current,
          activeCampaignId: campaignId,
        })
      ) {
        return;
      }
      const errorPanel = {
        status: 'error',
        campaignId,
        mailingId: row.mailingId,
        responses: [],
      };
      setResponsePanel(errorPanel);
      responsePanelRef.current = errorPanel;
    }
  };

  const handleCloseResponses = () => {
    responseRequestTrackerRef.current.invalidate();
    pendingResponsePanelRevealOwnerRef.current = null;
    const closed = createClosedAdminPublicResponsePanel();
    setResponsePanel(closed);
    responsePanelRef.current = closed;
  };

  const handleRequestArchive = async () => {
    if (!campaignId || !canRequestAdminProspectCampaignLetterArchive(campaign)) return;
    if (requestArchiveLockRef.current || requestingArchive || retryingArchive) return;
    // Never auto-rebuild a ready current archive from the failed/recovery path.
    requestArchiveLockRef.current = true;
    setRequestingArchive(true);
    setArchiveActionErrorKey(null);
    setArchivePollTimedOut(false);
    archivePollStartedAtRef.current = Date.now();
    try {
      const operationId = requestArchiveAttemptRef.current.resolveOperationId();
      const data = await requestAdminProspectCampaignLetterArchive(
        buildRequestAdminProspectCampaignLetterArchiveRequest({ campaignId, operationId }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              letterArchiveStatus: data.letterArchiveStatus,
              letterArchiveCountExpected: data.letterArchiveCountExpected,
              letterArchiveCountGenerated: data.letterArchiveCountGenerated,
              letterArchiveProgressPercent: data.letterArchiveProgressPercent,
              letterArchiveRecoverable: data.letterArchiveRecoverable === true,
            }
          : prev,
      );
      if (data.letterArchiveStatus === 'building') {
        setArchivePollTimedOut(false);
      }
      if (data.letterArchiveStatus === 'ready' || data.letterArchiveStatus === 'failed') {
        requestArchiveAttemptRef.current.reset();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setArchiveActionErrorKey(adminProspectCampaignLetterArchiveErrorKey(err));
    } finally {
      requestArchiveLockRef.current = false;
      if (mountedRef.current) setRequestingArchive(false);
    }
  };

  const handleRetryArchive = async () => {
    if (!campaignId || !canRetryAdminProspectCampaignLetterArchive(campaign)) return;
    if (retryArchiveLockRef.current || retryingArchive || requestingArchive) return;
    retryArchiveLockRef.current = true;
    setRetryingArchive(true);
    setArchiveActionErrorKey(null);
    setArchivePollTimedOut(false);
    archivePollStartedAtRef.current = Date.now();
    try {
      // Retain one operationId across the same ambiguous recovery attempt.
      const operationId = retryArchiveAttemptRef.current.resolveOperationId();
      const data = await retryAdminProspectCampaignLetterArchive(
        buildRetryAdminProspectCampaignLetterArchiveRequest({ campaignId, operationId }),
      );
      if (!mountedRef.current) return;
      setCampaign((prev) =>
        prev
          ? {
              ...prev,
              letterArchiveStatus: data.letterArchiveStatus,
              letterArchiveCountExpected: data.letterArchiveCountExpected,
              letterArchiveCountGenerated: data.letterArchiveCountGenerated,
              letterArchiveProgressPercent: data.letterArchiveProgressPercent,
              letterArchiveRecoverable: data.letterArchiveRecoverable === true,
            }
          : prev,
      );
      if (data.letterArchiveStatus === 'building' || data.recovered === true) {
        setArchivePollTimedOut(false);
      }
      // Definitive terminal outcomes (ready / failed enqueue / exhausted) clear the
      // retained operationId so the next click starts a new logical recovery attempt.
      // Duplicate alone is not enough: an enqueue_failed receipt replay is duplicate
      // with recovered=false and must reset so the client is not stuck.
      if (
        data.letterArchiveStatus === 'ready' ||
        data.letterArchiveStatus === 'failed' ||
        data.exhausted === true
      ) {
        retryArchiveAttemptRef.current.reset();
      } else if (data.duplicate === true && data.recovered !== true) {
        retryArchiveAttemptRef.current.reset();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setArchiveActionErrorKey(adminProspectCampaignLetterArchiveErrorKey(err));
    } finally {
      retryArchiveLockRef.current = false;
      if (mountedRef.current) setRetryingArchive(false);
    }
  };

  const handleDownloadArchive = async () => {
    if (!campaignId || !isCampaignLetterArchiveDownloadable(campaign)) return;
    if (downloadArchiveLockRef.current || downloadingArchive) return;
    setDownloadingArchive(true);
    setArchiveActionErrorKey(null);
    try {
      const outcome = await runAdminProspectCampaignLetterDownloadAttempt({
        inFlightRef: downloadArchiveLockRef,
        request: buildGetAdminProspectCampaignLetterArchiveDownloadUrlRequest({ campaignId }),
        requestDownloadUrl: getAdminProspectCampaignLetterArchiveDownloadUrl,
      });
      if (!mountedRef.current) return;
      if (outcome.outcome === 'in_flight') return;
      if (outcome.outcome === 'popup_blocked') {
        setArchiveActionErrorKey('admin.prospection.campaign.letters.archive.popupBlocked');
      } else if (outcome.outcome === 'expired') {
        setArchiveActionErrorKey('admin.prospection.campaign.letters.archive.downloadExpired');
      } else if (outcome.outcome === 'error') {
        setArchiveActionErrorKey(
          outcome.error
            ? adminProspectCampaignLetterArchiveErrorKey(outcome.error)
            : 'admin.prospection.campaign.letters.archive.downloadError',
        );
      }
    } finally {
      if (mountedRef.current) setDownloadingArchive(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.campaign.detail.loading')}
        </p>
      </div>
    );
  }

  if (status === 'error' || !campaign) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
          {t(adminProspectCampaignErrorKey(errorKind))}
        </p>
        <Link to="/admin/prospection/campaigns" className="text-sm font-semibold text-indigo-700 hover:underline">
          {t('admin.prospection.campaign.detail.back')}
        </Link>
      </div>
    );
  }

  const buildStatus = campaign.buildStatus;
  const targetingChips = describeAdminProspectTargetingChips(campaign.targetingSnapshot, t);
  const letterTemplateLabel = formatCampaignLetterTemplateLabel(campaign, t);
  const canGenerateLetters = canGenerateAdminProspectCampaignLetters(campaign);
  const canPrepareMailings = canPrepareAdminProspectCampaignMailings(campaign);
  const canRetryBuild = canRetryAdminProspectCampaignBuild(campaign);
  const canRetryMailingBuild = canRetryAdminProspectCampaignMailingBuild(campaign);
  const zeroMailable = campaignHasZeroMailableRecipients(campaign);
  const showMailingsPrepareSection =
    buildStatus === 'ready' &&
    (campaignHasMailingReadinessSummary(campaign) ||
      campaign.mailingBuildStatus === 'building' ||
      campaign.mailingBuildStatus === 'failed' ||
      campaign.mailingBuildStatus === 'ready');
  const generateBusy = generatingLetters || campaign.letterBuildStatus === 'building';
  const generateLabel =
    campaign.letterBuildStatus === 'failed' ||
    (campaign.letterBuildStatus === 'ready' && campaign.letterVersion != null)
      ? t('admin.prospection.campaign.letters.regenerate')
      : t('admin.prospection.campaign.letters.generate');
  const mailingRows = buildCampaignPdfLetterRows({ mailings, targets });

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              {campaign.name || t('admin.prospection.campaign.detail.title')}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{t('admin.prospection.campaign.detail.subtitle')}</p>
          </div>
        </div>
        <span
          className={`inline-flex self-start rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${adminProspectCampaignBuildStatusBadgeClass(buildStatus)}`}
        >
          {adminProspectCampaignBuildStatusLabel(buildStatus, t)}
        </span>
      </div>


      <dl className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.field.selectionMode')}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">
            {adminProspectCampaignSelectionModeLabel(campaign.selectionMode, t)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.field.selectedCount')}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">
            {campaign.selectedCountExpected ?? ADMIN_DATE_PLACEHOLDER}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.field.targetCount')}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">{campaign.targetCount ?? ADMIN_DATE_PLACEHOLDER}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.field.createdAt')}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">{formatAdminDate(campaign.createdAt, language)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('admin.prospection.campaign.field.snapshotCompletedAt')}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">
            {formatAdminDate(campaign.snapshotCompletedAt, language)}
          </dd>
        </div>
        {letterTemplateLabel ? (
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('admin.prospection.campaign.field.letterTemplate')}
            </dt>
            <dd className="mt-1 text-sm text-slate-900">{letterTemplateLabel}</dd>
          </div>
        ) : null}
      </dl>

      {campaign.selectionMode === 'all_matching' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="text-sm font-semibold text-slate-900">
            {t('admin.prospection.campaign.detail.targetingTitle')}
          </h2>
          {targetingChips.length > 0 ? (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {targetingChips.map((chip) => (
                <li
                  key={chip.id}
                  className="rounded-full bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200"
                >
                  {chip.label}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-slate-500">{t('admin.prospection.campaign.create.targetingEmpty')}</p>
          )}
        </div>
      ) : null}

      {buildStatus === 'building' ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" role="status">
          <p className="font-semibold">{t('admin.prospection.campaign.detail.buildingTitle')}</p>
          <p className="mt-1">{t('admin.prospection.campaign.detail.buildingBody')}</p>
          <p className="mt-2">
            {t('admin.prospection.campaign.detail.buildingExpected').replace(
              '{count}',
              String(campaign.selectedCountExpected ?? ADMIN_DATE_PLACEHOLDER),
            )}
          </p>
          <p className="mt-2">{t('admin.prospection.campaign.detail.buildingReadinessPending')}</p>
          {pollTimedOut ? (
            <div className="mt-3 space-y-2">
              <p className="font-medium">{t('admin.prospection.campaign.detail.buildingTimeout')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleRefreshCampaignStatus}
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {t('admin.prospection.campaign.detail.refreshStatus')}
                </button>
                {canRetryBuild ? (
                  <button
                    type="button"
                    onClick={handleRetryBuild}
                    disabled={retryingBuild}
                    aria-busy={retryingBuild}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  >
                    {retryingBuild
                      ? t('admin.prospection.campaign.detail.retryingPreparation')
                      : t('admin.prospection.campaign.detail.retryPreparation')}
                  </button>
                ) : null}
              </div>
              {retryBuildErrorKey ? (
                <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
                  {t(retryBuildErrorKey)}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {buildStatus === 'ready' && campaignHasMailingReadinessSummary(campaign) ? (
        <CampaignMailingReadinessSummary summary={campaign.mailingReadinessSummary} t={t} />
      ) : null}

      {buildStatus === 'ready' && !campaignHasMailingReadinessSummary(campaign) ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.prospection.campaign.readiness.legacyUnavailable')}
        </p>
      ) : null}

      {buildStatus === 'failed' ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-950" role="alert">
          <p className="font-semibold">{t('admin.prospection.campaign.detail.failedTitle')}</p>
          <p className="mt-1">{adminProspectCampaignFailureMessage(campaign.failureCode, t)}</p>
          {canRetryBuild ? (
            <div className="mt-3 space-y-2">
              <button
                type="button"
                onClick={handleRetryBuild}
                disabled={retryingBuild}
                aria-busy={retryingBuild}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              >
                {retryingBuild
                  ? t('admin.prospection.campaign.detail.retryingPreparation')
                  : t('admin.prospection.campaign.detail.retryPreparation')}
              </button>
              {retryBuildErrorKey ? (
                <p className="rounded-lg border border-rose-200 bg-white/70 p-3 text-sm text-rose-900" role="alert">
                  {t(retryBuildErrorKey)}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {showMailingsPrepareSection ? (
        <CampaignMailingsPrepareSection
          campaign={campaign}
          preparing={preparingMailings}
          prepareErrorKey={prepareActionErrorKey}
          mailingPollTimedOut={mailingPollTimedOut}
          canPrepare={canPrepareMailings}
          canRetryMailingBuild={canRetryMailingBuild}
          retryingMailingBuild={retryingMailingBuild}
          retryMailingBuildErrorKey={retryMailingBuildErrorKey}
          zeroMailable={zeroMailable}
          onPrepare={handlePrepareMailings}
          onRetryMailingBuild={handleRetryMailingBuild}
          onRefreshStatus={handleRefreshCampaignStatus}
          t={t}
        />
      ) : null}

      {campaignLettersSectionVisible(campaign) ? (
        <CampaignPdfLettersSection
          campaign={campaign}
          mailings={mailings}
          mailingRows={mailingRows}
          mailingStatus={mailingsStatus}
          mailingErrorKind={mailingsErrorKind}
          letterActionErrorKey={letterActionErrorKey}
          generating={generatingLetters}
          downloadingMailingId={downloadingMailingId}
          letterPollTimedOut={letterPollTimedOut}
          canGenerate={canGenerateLetters}
          generateDisabled={!canGenerateLetters || generateBusy}
          generateLabel={generateLabel}
          onGenerate={handleGenerateLetters}
          onDownload={handleDownloadLetter}
          onViewResponses={handleViewResponses}
          responsePanel={responsePanel}
          responsePanelElementRef={responsePanelElementRef}
          onCloseResponses={handleCloseResponses}
          onMailingsPrevious={handleMailingsPrevious}
          onMailingsNext={handleMailingsNext}
          mailingTokenStackLength={mailingsTokenStack.length}
          mailingNextPageToken={mailingsNextPageToken}
          language={language}
          t={t}
        />
      ) : null}

      {campaignLettersSectionVisible(campaign) ? (
        <CampaignLetterArchiveSection
          campaign={campaign}
          requesting={requestingArchive}
          retrying={retryingArchive}
          archiveActionErrorKey={archiveActionErrorKey}
          archivePollTimedOut={archivePollTimedOut}
          downloading={downloadingArchive}
          canRequest={canRequestAdminProspectCampaignLetterArchive(campaign)}
          canRetry={canRetryAdminProspectCampaignLetterArchive(campaign)}
          onRequest={handleRequestArchive}
          onRetry={handleRetryArchive}
          onDownload={handleDownloadArchive}
          onRefreshStatus={handleRefreshCampaignStatus}
          t={t}
        />
      ) : null}

      {buildStatus === 'ready' ? (
        <section className="space-y-3">
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
            <p className="font-semibold">{t('admin.prospection.campaign.snapshot.label')}</p>
            <p className="mt-1">{t('admin.prospection.campaign.snapshot.help')}</p>
          </div>

          {targetsStatus === 'loading' ? (
            <p className="text-sm text-slate-500">{t('admin.prospection.campaign.targets.loading')}</p>
          ) : null}
          {targetsStatus === 'paging' ? (
            <p className="text-sm text-slate-500">{t('admin.prospection.campaign.targets.paging')}</p>
          ) : null}
          {targetsStatus === 'error' ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
              {t(adminProspectCampaignErrorKey(targetsErrorKind))}
            </p>
          ) : null}
          {targetsStatus === 'empty' ? (
            <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              {t('admin.prospection.campaign.targets.empty')}
            </p>
          ) : null}

          {targets.length > 0 && (targetsStatus === 'ready' || targetsStatus === 'paging') ? (
            <>
              <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="min-w-[14rem] whitespace-nowrap px-3 py-2">
                        {t('admin.prospection.field.address')}
                      </th>
                      <th className="px-3 py-2">{t('admin.prospection.field.city')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.field.postalCode')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.field.numberOfUnits')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.field.ownerName')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.field.ownerMailingAddress')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.campaign.readiness.column')}</th>
                      <th className="px-3 py-2">{t('admin.prospection.campaign.targets.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targets.map((item) => {
                      const prospectId = item.prospectId || item.targetId;
                      return (
                        <tr key={item.targetId || prospectId} className="border-t border-slate-100">
                          <td className="whitespace-nowrap px-3 py-2">
                            {formatCampaignTargetAddress(item) || ADMIN_DATE_PLACEHOLDER}
                          </td>
                          <td className="px-3 py-2">{item.property?.city || ADMIN_DATE_PLACEHOLDER}</td>
                          <td className="px-3 py-2">{item.property?.postalCode || ADMIN_DATE_PLACEHOLDER}</td>
                          <td className="px-3 py-2">{formatCampaignTargetUnits(item)}</td>
                          <td className="px-3 py-2">{formatCampaignTargetOwnerName(item)}</td>
                          <td className="px-3 py-2">{formatCampaignTargetOwnerMailing(item)}</td>
                          <td className="px-3 py-2">
                            <CampaignTargetReadinessBlock mailingReadiness={item.mailingReadiness} t={t} />
                          </td>
                          <td className="px-3 py-2">
                            <CampaignTargetProspectAction
                              prospectId={prospectId}
                              mailingReadiness={item.mailingReadiness}
                              t={t}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <ul className="space-y-3 md:hidden">
                {targets.map((item) => {
                  const prospectId = item.prospectId || item.targetId;
                  return (
                    <li key={item.targetId || prospectId} className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="font-semibold text-slate-900">
                        {formatCampaignTargetAddress(item) || ADMIN_DATE_PLACEHOLDER}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        {formatCampaignTargetLocality(item) || ADMIN_DATE_PLACEHOLDER}
                      </p>
                      {item.mailingReadiness ? (
                        <div className="mt-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {t('admin.prospection.campaign.readiness.mobileLabel')}
                          </p>
                          <div className="mt-1">
                            <CampaignTargetReadinessBlock
                              mailingReadiness={item.mailingReadiness}
                              t={t}
                              compact
                            />
                          </div>
                        </div>
                      ) : null}
                      <dl className="mt-3 space-y-1 text-sm text-slate-600">
                        <div>
                          <dt className="inline text-slate-500">
                            {t('admin.prospection.field.numberOfUnits')}:{' '}
                          </dt>
                          <dd className="inline">{formatCampaignTargetUnits(item)}</dd>
                        </div>
                        <div>
                          <dt className="inline text-slate-500">{t('admin.prospection.field.ownerName')}: </dt>
                          <dd className="inline">{formatCampaignTargetOwnerName(item)}</dd>
                        </div>
                        <div>
                          <dt className="text-slate-500">{t('admin.prospection.field.ownerMailingAddress')}</dt>
                          <dd>{formatCampaignTargetOwnerMailing(item)}</dd>
                        </div>
                      </dl>
                      {prospectId ? (
                        <div className="mt-3">
                          <CampaignTargetProspectAction
                            prospectId={prospectId}
                            mailingReadiness={item.mailingReadiness}
                            t={t}
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>

              {tokenStack.length > 0 || nextPageToken ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleTargetsPrevious}
                    disabled={tokenStack.length === 0 || targetsStatus === 'paging'}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {t('admin.prospection.campaign.list.prev')}
                  </button>
                  <button
                    type="button"
                    onClick={handleTargetsNext}
                    disabled={!nextPageToken || targetsStatus === 'paging'}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {t('admin.prospection.campaign.list.next')}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
